import React, { useCallback, useMemo } from 'react';
import { colors, useQueryParams } from '@insurancemenu/imenu-components-lib';
import { useHistory, useParams } from 'react-router-dom';
import { PrimaryActionDropdown, ListItem } from 'components/common/general/PrimaryActionDropdown';
import { AddEmployeesAction } from 'types/domainServiceV2';
import { ADD_EMPLOYEES_DROPDOWN_TEST_IDS } from '../constants';

interface Props {
  censusRedirectLink: string;
  availableAddEmployeesActions: AddEmployeesAction[];
}

export const AddEmployeesDropdown: React.FC<Props> = ({ censusRedirectLink, availableAddEmployeesActions }) => {
  const history = useHistory();
  const query = useQueryParams();
  const { periodId } = useParams<{ periodId: string }>();
  const cartId = query.get('cartId') || '';
  const groupId = query.get('groupId') || '';

  const redirectToSelfRegistrationLink = useCallback((): void => {
    history.push(`/employee-self-register-link/${periodId}?groupId=${groupId}`);
  }, [history, periodId, groupId, cartId]);

  const redirectToSingleEmployeeForm = useCallback((): void => {
    history.push(`/add-employee-form/${groupId}`);
  }, [history, groupId]);

  const redirectToCensusFileUpload = useCallback((): void => {
    history.push(censusRedirectLink);
  }, [history, censusRedirectLink]);

  const listItems: ListItem[] = useMemo(
    () => [
      {
        title: '1. Census File Upload',
        isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.CENSUS_UPLOAD),
        text: "Upload a file with multiple employee details. We'll guide you through validation and review.",
        buttonLabel: 'Continue',
        buttonColor: colors.orange[600],
        onClick: redirectToCensusFileUpload,
        dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.CENSUS_UPLOAD_BUTTON,
      },
      {
        title: '2. Self-Registration Link',
        isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.SELF_REGISTER_LINK),
        text: 'Generate a shareable link for employees to self-register. Share it with the employer to distribute.',
        buttonLabel: 'Continue',
        buttonColor: colors.green[600],
        onClick: redirectToSelfRegistrationLink,
        dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.SELF_REGISTRATION_BUTTON,
      },
      {
        title: '3. Single Employee Form',
        isAvailable: availableAddEmployeesActions.includes(AddEmployeesAction.BASIC_FORM),
        text: 'Add one employee using a short form. You can keep adding more afterward.',
        buttonLabel: 'Continue',
        buttonColor: colors.blue[600],
        onClick: redirectToSingleEmployeeForm,
        dataTestId: ADD_EMPLOYEES_DROPDOWN_TEST_IDS.SINGLE_EMPLOYEE_BUTTON,
      },
    ],
    [redirectToCensusFileUpload, redirectToSelfRegistrationLink, redirectToSingleEmployeeForm],
  );

  return (
    <PrimaryActionDropdown
      buttonTitle="Add Employees"
      buttonWidth="26rem"
      dropdownTitle="Add Employees"
      dropdownSubtitle="Pick the method that fits your workflow — all employees will appear in the same list"
      listItems={listItems}
    />
  );
};
